import { Link as LinkIcon } from "lucide-react";
import { Image } from "@themes/pollinator/ui/Image";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "@platform/features/marketing";
import { newsType } from "../../news.type";

type WithChildren = { children?: React.ReactNode; [key: string]: unknown };

/**
 * A picture in a post, with an optional credit under it.
 *
 * Written in a post as
 * `<Figure src="…" alt="…">Image courtesy of Fauxels via [Pexels](https://www.pexels.com)</Figure>`.
 * The design draws it the full width of the text, 3:2, with a 12px corner,
 * 48px clear of the paragraphs either side, and the credit 16px under it
 * after a small link mark. Without a picture its place is the design's plain
 * panel.
 */
export function Figure({ src, alt = "", children }: { src?: string; alt?: string; children?: React.ReactNode }) {
  return (
    <figure className="my-12 flex flex-col gap-4 first:mt-0 last:mb-0" data-testid="news-figure">
      <div className="aspect-[3/2] w-full overflow-hidden rounded-xl bg-brand-100">
        {src && <Image src={src} alt={alt} className="h-full w-full" imageClassName="object-cover" />}
      </div>
      {children && (
        <figcaption className="flex items-start gap-1.5">
          <LinkIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-600" strokeWidth={1.67} />
          <Type variant="caption" as="span" className={`${newsType.credit} [&_p]:m-0 [&_p]:inline [&_p]:text-inherit`}>
            {children}
          </Type>
        </figcaption>
      )}
    </figure>
  );
}

/**
 * A block set apart from the text in the pale yellow panel, with a heading.
 *
 * Written in a post as `<Callout title="…">`, a blank line, the paragraphs,
 * a blank line, then `</Callout>`.
 */
export function Callout({ title, children }: { title?: string; children?: React.ReactNode }) {
  return (
    <aside className="my-12 rounded-2xl bg-brand-100 p-6 first:mt-0 last:mb-0 lg:p-8" data-testid="news-callout">
      {title && (
        <Type variant="h4" as="p" className={`${newsType.calloutHeading} mb-4`}>
          {title}
        </Type>
      )}
      {children}
    </aside>
  );
}

const body = `${marketingType.body} max-w-none`;

/**
 * How a post's writing is drawn, in place of the support articles' defaults.
 *
 * Paragraphs are the site's body copy, 16px apart, as the design draws them
 * (its spacing guides say 18, but every paragraph in it is set 16 apart). A heading has 40px above
 * its capitals and 20px below them; its line is 45px for 27px of capitals, so
 * 9px of each is the line's own. A quote is the design's pull quote: a 2px
 * green rule, then the words large and italic and, as a paragraph of its own
 * under them, who said it.
 */
export const newsPostComponents = {
  p: ({ children, ...props }: WithChildren) => (
    <Type {...props} variant="body1" className={`${body} mb-4 last:mb-0`}>
      {children}
    </Type>
  ),
  h1: ({ children, ...props }: WithChildren) => (
    <Type {...props} variant="h3" as="h2" className={`${newsType.heading} mt-[31px] mb-[11px] first:mt-0`}>
      {children}
    </Type>
  ),
  h2: ({ children, ...props }: WithChildren) => (
    <Type {...props} variant="h3" as="h2" className={`${newsType.heading} mt-[31px] mb-[11px] first:mt-0`}>
      {children}
    </Type>
  ),
  h3: ({ children, ...props }: WithChildren) => (
    <Type {...props} variant="h4" as="h3" className={`${newsType.calloutHeading} mt-8 mb-4 first:mt-0`}>
      {children}
    </Type>
  ),
  blockquote: ({ children, ...props }: WithChildren) => (
    <blockquote {...props} className="my-12 flex gap-5 first:mt-0 last:mb-0" data-testid="news-quote">
      <span aria-hidden className="w-0.5 shrink-0 bg-brand-600" />
      <div
        className={[
          "flex flex-col gap-6 py-2 lg:gap-8",
          "[&>p]:m-0",
          // The words, then who said them.
          "[&>p:first-child]:text-[24px] [&>p:first-child]:leading-[30px] [&>p:first-child]:font-medium [&>p:first-child]:italic [&>p:first-child]:tracking-[-0.48px] [&>p:first-child]:[font-feature-settings:normal]",
          "lg:[&>p:first-child]:text-[36px] lg:[&>p:first-child]:leading-[45px] lg:[&>p:first-child]:tracking-[-0.72px]",
          // Trimmed to its capitals, as the design measures a 36px line: the
          // rule then runs from the top of the words to the attribution.
          "lg:[&>p:first-child]:-my-[9px]",
        ].join(" ")}
      >
        {children}
      </div>
    </blockquote>
  ),
  ol: ({ children, ...props }: WithChildren) => (
    <ol {...props} className={`${body} mb-4 flex list-decimal flex-col gap-2 pl-[30px] last:mb-0`}>
      {children}
    </ol>
  ),
  ul: ({ children, ...props }: WithChildren) => (
    <ul {...props} className={`${body} mb-4 flex list-disc flex-col gap-2 pl-[30px] last:mb-0`}>
      {children}
    </ul>
  ),
  li: ({ children, ...props }: WithChildren) => (
    <li {...props} className="pl-0 [&>p]:mb-0">
      {children}
    </li>
  ),
  img: ({ src, alt }: WithChildren) => <Figure src={src as string} alt={alt as string} />,
  a: ({ children, href, ...props }: WithChildren) => (
    <a {...props} href={href as string} className="underline decoration-1 underline-offset-2 hover:text-brand-600">
      {children}
    </a>
  ),
  strong: ({ children, ...props }: WithChildren) => (
    <strong {...props} className="font-bold">
      {children}
    </strong>
  ),
  Figure,
  Callout,
};
